<template>
    <v-card-text>
        <PlantConfigDialog
            :dialog.sync="configPlantDialog"
            :plantNodeId="configingPlant.node ? configingPlant.node.id : null"
            v-on:item-added="addItemConfirmed"
        ></PlantConfigDialog>

        <!-- Selected Items -->
        <draggable v-model="selectedPlants" v-bind="dragOptions" handle=".handle">
            <transition-group type="transition">
                <v-col
                    v-for="(item, index) in selectedPlants"
                    :key="index + 0"
                    cols="12"
                    class="px-0 pb-1"
                >
                    <v-hover>
                        <template v-slot="{ hover }">
                            <v-card
                                :elevation="hover ? 6 : 1"
                                class="px-3"
                                width="100%"
                                outlined
                                height="100%"
                            >
                                <!-- title -->
                                <div>
                                    <v-row class="align-center justify-space-between">
                                        <v-col cols="12" md="4" class="pa-0">
                                            <v-list-item class="pr-1">
                                                <v-list-item-action class="mx-2">
                                                    <v-icon class="handle mr-2">menu</v-icon>
                                                </v-list-item-action>
                                                <v-list-item-content>{{ item.node.plant.name }}</v-list-item-content>
                                                <v-list-item-action class="hidden-md-and-up">
                                                    <v-btn
                                                        fab
                                                        small
                                                        text
                                                        @click="deleteItem(item)"
                                                        class="error--text"
                                                    >
                                                        <v-icon>close</v-icon>
                                                    </v-btn>
                                                </v-list-item-action>
                                            </v-list-item>
                                        </v-col>

                                        <v-col cols="12" md="5" class="pa-0">
                                            <v-row align="center" justify="center" class="my-0">
                                                <v-chip class="mr-1 my-1 primary">
                                                    <v-avatar left>
                                                        <v-icon dark small>straighten</v-icon>
                                                    </v-avatar>
                                                    {{ item.node.potSize ? item.node.potSize.potSizeCm + "cm" : "*" }}
                                                </v-chip>
                                                <v-chip class="mr-1 my-1 primary">
                                                    <v-avatar left>
                                                        <v-icon dark small>color_lens</v-icon>
                                                    </v-avatar>
                                                    {{ item.node.color ? item.node.color.color : "*" }}
                                                </v-chip>
                                                <v-chip class="mr-1 my-1 primary">
                                                    <v-avatar left>
                                                        <v-icon dark small>blur_on</v-icon>
                                                    </v-avatar>
                                                    {{ item.node.environment ? item.node.environment.environment : "*" }}
                                                </v-chip>
                                                <v-chip class="mr-1 my-1 primary">
                                                    <v-avatar left>
                                                        <v-icon dark small>turned_in_not</v-icon>
                                                    </v-avatar>
                                                    {{ item.node.tag ? item.node.tag.tag : "*" }}
                                                </v-chip>
                                            </v-row>
                                        </v-col>

                                        <v-col cols="12" md="3" class="pa-0">
                                            <v-list-item class="px-0">
                                                <v-list-item-content class="py-0">
                                                    <v-row class="justify-center align-center">
                                                        <v-btn
                                                            :disabled="item.node.numPlants<=1"
                                                            fab
                                                            small
                                                            text
                                                            class="grey--text text--darken-2"
                                                            @click="item.node.numPlants-=1"
                                                        >
                                                            <v-icon>remove_circle_outline</v-icon>
                                                        </v-btn>
                                                        <div class="title">{{ item.node.numPlants }}</div>
                                                        <v-btn
                                                            fab
                                                            small
                                                            text
                                                            class="grey--text text--darken-2"
                                                            @click="item.node.numPlants+=1"
                                                        >
                                                            <v-icon>add_circle_outline</v-icon>
                                                        </v-btn>
                                                    </v-row>
                                                </v-list-item-content>
                                                <v-list-item-action
                                                    class="hidden-sm-and-down mx-0 my-2"
                                                >
                                                    <v-btn
                                                        fab
                                                        small
                                                        text
                                                        @click="deleteItem(item)"
                                                        class="error--text mr-2"
                                                    >
                                                        <v-icon>close</v-icon>
                                                    </v-btn>
                                                </v-list-item-action>
                                            </v-list-item>
                                        </v-col>
                                    </v-row>
                                </div>
                            </v-card>
                        </template>
                    </v-hover>
                </v-col>
            </transition-group>
        </draggable>

        <!-- Choices table -->
        <v-row>
            <!-- Header -->
            <v-col cols="12">
                <v-text-field
                    v-model="search"
                    append-outer-icon="search"
                    label="Search"
                    clearable
                    hide-details
                    class="pl-2"
                    @keydown.enter="refreshCurrentPage()"
                    @click:append-outer="refreshCurrentPage()"
                    @click:clear="onClearSearch()"
                ></v-text-field>
            </v-col>
            <v-col cols="12">
                <v-card>
                    <v-data-table
                        :headers="headers"
                        :loading="$apollo.queries.tenantPlants.loading"
                        :items="tenantPlants ? tenantPlants.edges : []"
                        :options.sync="dataTableOptions"
                        loading-text="Loading... Please wait"
                        :server-items-length="tenantPlants ? tenantPlants.totalCount : 0"
                        :items-per-page="defaultItemsPerPage"
                        :footer-props="{
                                        'items-per-page-options': [10,25,50,75,100],
                                        'disable-pagination': $apollo.queries.tenantPlants.loading
                                        }"
                    >
                        <!-- <template v-slot:item.node.name="{ item }">
                            {{ item.node.name }}
                            <v-btn
                                icon
                                small
                                color="grey"
                                @click="goToPlantDetail(item.node.id)"
                            >
                                <v-icon small>launch</v-icon>
                            </v-btn>
                        </template>-->

                        <template v-slot:item.node.numProducts="{ item }">
                            <span
                                :class="item.node.numProducts <= 0 ? 'red--text' : ''"
                            >{{ item.node.numProducts }}</span>
                        </template>
                        <template v-slot:item.action="{ item }">
                            <v-btn small outlined rounded color="primary" @click="addItem(item)">Add</v-btn>
                            <v-btn icon color="primary" @click="goToPlantDetail(item.node.id)">
                                <v-icon>launch</v-icon>
                            </v-btn>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
    </v-card-text>
</template>

<script>
import gql from "graphql-tag";
import draggable from "vuedraggable";
import PlantConfigDialog from "@/components/plants/PlantConfigDialog.vue";

export default {
    name: "product-item-editor",
    props: ["productNodeId", "initialSelected"],
    components: {
        draggable,
        PlantConfigDialog
    },

    apollo: {
        tenantPlants: {
            query: gql`
                query tenantPlants(
                    $first: Int
                    $last: Int
                    $before: String
                    $after: String
                    $orderBy: String
                    $search: String
                ) {
                    tenantPlants(
                        first: $first
                        last: $last
                        before: $before
                        after: $after
                        search: $search
                        orderBy: $orderBy
                    ) {
                        totalCount
                        pageInfo {
                            startCursor
                            endCursor
                        }
                        edges {
                            node {
                                id
                                name
                                plantSpecies {
                                    id
                                    name
                                }
                                numProducts
                            }
                        }
                    }
                }
            `,
            variables() {
                return {
                    first: 10
                };
            },
            // Additional options here
            fetchPolicy: "cache-and-network",
            update: (data) => data.tenantPlants,
            skip() {
                return true;
            }

            // pollInterval: 300 // ms
        }
    },

    data: function () {
        return {
            // search: "",
            // isLoadingPlants: false,
            configPlantDialog: false,
            selectedPlants: [],
            configingPlant: {},

            search: "",

            defaultItemsPerPage: 10,
            dataTableOptions: {},
            page: 1,

            tenantPlants: {},

            headers: [
                {
                    text: "Plant Species",
                    align: "left",
                    sortable: true,
                    value: "node.plantSpecies.name"
                },
                {
                    text: "Plant",
                    align: "left",
                    sortable: true,
                    value: "node.name"
                },
                {
                    text: "#Products",
                    align: "left",
                    sortable: true,
                    value: "node.numProducts"
                },
                {
                    text: "Actions",
                    align: "right",
                    value: "action",
                    sortable: false
                }
            ]
        };
    },
    computed: {
        dragOptions() {
            return {
                animation: 200,
                group: "description",
                disabled: false,
                ghostClass: "ghost",
                handle: ".handle"
            };
        }
    },
    watch: {
        dataTableOptions: {
            handler() {
                this.refreshCurrentPage();
            },
            deep: true
        }
    },
    created() {
        this.initialize();
    },
    methods: {
        initialize() {
            this.selectedPlants = JSON.parse(
                JSON.stringify(this.initialSelected)
            );
            // this.fetchPlants();
        },

        // fetchPlants() {
        //     this.isLoadingPlants = true;

        //     // fetch plant species
        //     this.$store
        //         .dispatch("plants/fetchPlants")
        //         .then(() => {})
        //         .catch(error => {
        //             /*handle errors*/
        //             console.log(error);
        //         })
        //         .finally(() => (this.isLoadingPlants = false));
        // },

        onClearSearch() {
            this.search = null;

            this.refreshCurrentPage();
        },

        refreshCurrentPage() {
            const {
                sortBy,
                sortDesc,
                page,
                itemsPerPage
            } = this.dataTableOptions;

            var _first = itemsPerPage;
            var _last = null;
            var _before = null;
            var _after = null;
            var _orderBy = null;

            if (page < this.page) {
                _first = null;
                _last = itemsPerPage;
                _before = this.tenantPlants.pageInfo.startCursor;
                _after = null;
            }

            if (page > this.page) {
                _first = itemsPerPage;
                _last = null;
                _before = null;
                _after = this.tenantPlants.pageInfo.endCursor;
            }

            switch (sortBy[0]) {
                case "node.plantSpecies.name":
                    _orderBy = "plantSpeciesName";
                    break;
                case "node.name":
                    _orderBy = "plantName";
                    break;
                case "node.numProducts":
                    _orderBy = "numProducts";
                    break;
                default:
                    _orderBy = null;
                    break;
            }

            if (sortDesc[0] === true) {
                _orderBy = "-" + _orderBy;
            }

            this.$apollo.queries.tenantPlants.setVariables({
                first: _first,
                last: _last,
                before: _before,
                after: _after,
                orderBy: _orderBy,
                search: this.search
            });

            // set skip false to execute the query
            this.$apollo.queries.tenantPlants.skip = false;
            this.$apollo.queries.tenantPlants.refresh();

            // update this.page
            this.page = page;
        },

        addItem(item) {
            // // sort plantColors
            // item.node.plantColorSet.edges.sort((a, b) => {
            //     return a.node.color.color.toLowerCase() ==
            //         b.node.color.color.toLowerCase()
            //         ? 0
            //         : a.node.color.color.toLowerCase() >
            //           b.node.color.color.toLowerCase()
            //         ? 1
            //         : -1;
            // });

            // // sort plantPotSizes
            // item.node.plantPotSizeSet.edges.sort((a, b) => {
            //     return a.node.potSize.potSizeCm == b.node.potSize.potSizeCm
            //         ? 0
            //         : a.node.potSize.potSizeCm > b.node.potSize.potSizeCm
            //         ? 1
            //         : -1;
            // });

            // // sort plantEnvironments
            // item.node.plantEnvironmentSet.edges.sort((a, b) => {
            //     return a.node.environment.environment.toLowerCase() ==
            //         b.node.environment.environment.toLowerCase()
            //         ? 0
            //         : a.node.environment.environment.toLowerCase() >
            //           b.node.environment.environment.toLowerCase()
            //         ? 1
            //         : -1;
            // });

            // // sort plantTags
            // item.node.plantTagSet.edges.sort((a, b) => {
            //     return a.node.tag.orderId == b.node.tag.orderId
            //         ? 0
            //         : a.node.tag.orderId > b.node.tag.orderId
            //         ? 1
            //         : -1;
            // });

            this.configingPlant = item;
            this.configPlantDialog = true;
        },

        addItemConfirmed(item) {
            // console.log(item)

            // check if item already exists
            var index = this.selectedPlants.findIndex(function (element) {
                return (
                    element.node.plant.id === item.plant.id &&
                    element.node.color.id === item.color.id &&
                    element.node.potSize.id === item.potSize.id &&
                    element.node.environment.id === item.environment.id &&
                    (element.node.tag ? element.node.tag.id : null) ===
                        (item.tag ? item.tag.id : null)
                );
            });

            if (index > -1) {
                // this.selectedPlants[index].node.numPlants += 1;

                // show snackbar
                const payload = {
                    color: "error",
                    message: `This plant already exists!`
                };
                this.$store.dispatch("snackbar/showMessage", payload);
            } else {
                this.selectedPlants.push({
                    node: {
                        ...item,
                        numPlants: 1
                    }
                });

                this.configPlantDialog = false;
            }
        },

        deleteItem(item) {
            // console.log(item.node);
            
            var index = this.selectedPlants.findIndex(function (element) {
                // console.log(element)

                return (
                    element.node.plant.id === item.node.plant.id &&
                    element.node.color.id === item.node.color.id &&
                    element.node.potSize.id === item.node.potSize.id &&
                    element.node.environment.id === item.node.environment.id &&
                    (element.node.tag ? element.node.tag.id : null) ===
                        (item.node.tag ? item.node.tag.id : null)
                );
            });

            // console.log(index);

            if (index > -1) {
                this.selectedPlants.splice(index, 1);
            }
        },

        goToPlantDetail(plantNodeId) {
            // this.$router.push({
            //     name: "plant-detail",
            //     params: { id: plant.id }
            // });

            let routeData = this.$router.resolve({
                name: "plant-detail",
                params: { id: plantNodeId }
            });
            window.open(routeData.href, "_blank");
        }
    }
};
</script>



<style scoped>
.handle {
    float: left;
    padding-top: 8px;
    padding-bottom: 8px;
    border: none;
    background-color: transparent;
}
</style>
